import React from 'react';
import {
  SizeVariant,
  BaseComponentProps,
} from '../types/componentThemes';

export interface SpinnerProps extends BaseComponentProps {
  /** Spinner size */
  size?: SizeVariant;
  /** Custom color (defaults to primary) */
  color?: string;
}

/**
 * Spinner Component
 * 
 * An animated loading spinner with configurable size.
 * Uses primary color from design system by default.
 * 
 * @example
 * ```tsx
 * <Spinner size="md" />
 * <Spinner size="lg" color="text-secondary-600" />
 * ```
 */
export const Spinner: React.FC<SpinnerProps> = ({
  size = 'md',
  color = 'text-primary-600',
  className = '',
  style,
  testId,
}) => {
  // Size-specific classes
  const sizeClasses: Record<SizeVariant, string> = {
    sm: 'h-4 w-4',
    md: 'h-8 w-8',
    lg: 'h-12 w-12',
  };

  const spinnerClasses = [
    'animate-spin',
    'rounded-full',
    'border-2',
    'border-current',
    'border-t-transparent',
    sizeClasses[size],
    color,
    className,
  ].join(' ');

  return (
    <div
      className={spinnerClasses}
      style={style}
      data-testid={testId}
      role="status"
      aria-label="Loading"
    >
      <span className="sr-only">Loading...</span>
    </div>
  );
};
